<template>
  <div>
    <div :class="['overlay', {active: isLoading}]">
      <loading-spinner
        custom-id="loading_spinner_table_view"
        :loading-status="isLoading"
      />
    </div>
    <div class="flex-parent mb-5">
      <div class="switch-parent">
        <Switcher @onChangeType="getTypeTemplates" />
      </div>
      <div class="search-parent">
        <div class="e-input-group">
          <input
            id="inputSearch"
            v-model="search"
            class="e-input"
            name="input"
            type="text"
            placeholder="Search"
          >
          <span class="e-input-group-icon">
            <button
              id="btnSearch"
              :class="['btn', 'btn-default']"
              @click="startSearch"
            >
              <font-awesome-icon
                :icon="['far', 'search']"
                size="lg"
              />
            </button>
          </span>
        </div>
      </div>
      <div class="filter-parent">
        <div class="multi-select-parent d-flex flex-row">
          <label class="editor-filter">Editor </label>
          <ejs-multiselect
            id="multiselect"
            ref="prebuiltMultiSelectFilter"
            css-class="multiselect-filter-select"
            :allow-filtering="false"
            :allow-custom-value="false"
            mode="Box"
            :data-source="editorSelectorData"
            :value="prebuiltMultiSelectEditorFilterValues"
            :show-select-all="false"
            :read-only="true"
            @removed="editorFilterRemoved"
            @tagging="editorFilterAdded"
          />
        </div>
      </div>
      <div class="dropdown-filter-parent">
        <label class="sort-by">Sort by</label>
        <ejs-dropdownbutton
          id="dropdownSort"
          css-class="d-inline-flex align-items-center dropdown-border-color dropdown-sort-by"
          icon-css="\e993"
          :items="prebuiltItems"
          icon-position="right"
          :select="onDropdownSelect"
        >
          {{ currentOrder }}
        </ejs-dropdownbutton>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { Pager, PagerDropDown } from '@syncfusion/ej2-grids'
import Switcher from '../components/SwitchPreBuilt'
import { mapGetters } from 'vuex'
import LoadingSpinner from '@/components/LoadingSpinner'
import { iframeParentCommunication } from '@/mixins/iframeParentCommunication'

Pager.Inject(PagerDropDown)

export default {
  components: {
    Switcher,
    LoadingSpinner
  },
  mixins: [iframeParentCommunication],
  props: {
    onClickUse: Function,
    edit: Function,
    clone: Function,
    prebuiltClone: Function,
    remove: Function
  },
  data () {
    return {
      pageSizes: [12, 24, 48, 90],
      selectedTemplate: null,
      pristine: true,
      prebuiltItems: [
        {
          text: 'Last Updated',
          name: 'updateDate',
          direction: 'descending'
        },
        {
          text: 'First Updated',
          name: 'updateDate',
          direction: 'ascending'
        },
        {
          text: 'A to Z',
          name: 'name',
          direction: 'ascending'
        },
        {
          text: 'Z to A',
          name: 'name',
          direction: 'descending'
        }
      ],
      templateBuilderQuery: this.$helpers.getTemplateBuilderQuery(this.$route),
      search: '',
      typingTimer: null,
      deleteButton: {
        label: 'Ok',
        class: 'btn-danger',
        action: this.onConfirm
      },
      selectedForDeletion: -1,
      editorSelectorData: ['Studio Editor', 'Classic Drag & Drop', 'Custom Code']
    }
  },
  computed: {
    ...mapGetters({
      templates: 'getGlobalTemplates',
      templateContent: 'getTemplateContent',
      paging: 'getGlobalPaging',
      isLoading: 'getLoadingStatus',
      activeType: 'getActiveType',
      gridStateChangeData: 'getGridStateChangeData'
    }),
    currentOrder () {
      const { order } = this.paging
      if (order) {
        const sort = this.prebuiltItems.find(item => item.name === order.column && item.direction.startsWith(order.direction))
        if (sort) {
          return sort.text
        }
      }
      return ''
    },
    prebuiltMultiSelectEditorFilterValues () {
      const defaultFilter = this.$store.getters.getPrebuiltGridEditorFilter

      return defaultFilter
    }
  },
  watch: {
    search: {
      handler: function (newVal, oldVal) {
        if (newVal !== oldVal) {
          if (this.typingTimer !== null) {
            clearTimeout(this.typingTimer)
          }
          this.typingTimer = setTimeout(() => {
            this.startSearch()
          }, 600)
        }
      }
    }
  },
  beforeCreate () {
    this.$store.commit('SET_GRIDSELECTEDTEMPLATE', {})
    this.$store.commit('SET_TEMPLATE_PREVIEW_CONTENT', null)
  },
  created () {
    const currentPageSize = this.paging.pageSize
    const pageSize = this.getPageSizes()
    this.$store.commit('SET_GLOBAL_PAGING', { ...this.paging, pageSizes: this.pageSizes, pageSize: pageSize })
    if (currentPageSize !== pageSize) {
      setTimeout(() => {
        this.refresh()
      }, 100)
    }
    this.refresh()
    this.checkCompanyGlobalPermissions()
    this.setUserGlobalPermission()
  },
  mounted () {
    if (this.paging?.searchBox) {
      this.search = this.paging?.searchBox
    }
  },
  methods: {
    startSearch () {
      this.$store.commit('SET_GLOBAL_PAGING', { searchBox: this.search, page: 1 })

      // Only refreshing when the active view type is the prebuilt view & view the is
      if (this.activeViewType === 'prebuilt' && this.activeView === 'table') {
        this.$store.dispatch('setGridStateChangeData', { where: null })
        this.refresh()
      } else {
        this.loadTemplates()
      }
    },
    getPageSizes () {
      const pageSizeIndex = this.paging.pageSizes.findIndex(x => parseInt(x) === this.paging.pageSize)
      return (pageSizeIndex > -1) ? this.pageSizes[pageSizeIndex] : 12
    },
    getLoadingStatus (name) {
      return this.$store.getters.getLoadingStatus(name)
    },
    refresh (updateSavedTemplates = false) {
      const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : null
      if (userEmail) {
        const { paging } = this
        const { order } = paging
        const dataState = {
          action: {
            currentPage: paging.page
          },
          skip: 0,
          take: paging.pageSize,
          sorted: [
            {
              name: order.column,
              direction: order.direction
            }
          ]
        }
        if (updateSavedTemplates) {
          this.$store.dispatch('setTemplates', { userEmail, dataState })
        }
        this.$store.dispatch('setGlobalTemplates', { userEmail, dataState })
      }
    },
    loadTemplates () {
      const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : null
      if (userEmail) {
        const { paging } = this
        const { order } = paging
        const where = this.gridStateChangeData === null ? null : this.gridStateChangeData.state.where
        const dataState = {
          action: {
            currentPage: paging.page
          },
          skip: 0,
          take: paging.pageSize,
          sorted: [
            {
              name: order.column,
              direction: order.direction
            }
          ]
        }
        if (where != null) {
          Object.assign(dataState, { where: where })
        }
        this.$store.dispatch('setGlobalTemplates', { userEmail, dataState })
      }
    },
    onDropdownSelect ({ item }) {
      const order = {
        direction: item.direction,
        column: item.name
      }
      this.$store.commit('SET_GLOBAL_PAGING', { order, page: 1 })
      Vue.nextTick(() => {
        this.refresh()
      })
    },
    onPagerClick (data) {
      const { currentPage, newProp } = data
      if ('currentPage' in newProp) {
        this.$store.commit('SET_GLOBAL_PAGING', { page: currentPage })
        Vue.nextTick(() => {
          this.refresh()
        })
      }
    },
    onPageSizesChange (data) {
      if (this.pristine) {
        this.pristine = false
      } else {
        this.$store.commit('SET_GLOBAL_PAGING', { pageSize: data.pageSize, page: 1 })
        Vue.nextTick(() => {
          this.refresh()
        })
      }
    },
    onPreview (templateId) {
      this.$refs.previewTemplateDialog.openDialog()
      this.$store.dispatch('setPrebuiltTemplateOnPreview', { ...this.templateBuilderQuery, templateId })
    },
    onCloseDialog () {
      this.$store.commit('SET_TEMPLATE_PREVIEW_CONTENT', null)
    },
    openDialog (id) {
      this.selectedForDeletion = id
      this.$refs.deleteDialog.openDialog()
    },
    onConfirm () {
      if (this.selectedForDeletion > -1) {
        const prom = this.remove(this.selectedForDeletion)
        this.selectedForDeletion = -1
        prom.then(response => {
          if (response.success) {
            this.refresh()
          }
        })
      }
    },
    getTypeTemplates (name = '') {
      this.$store.commit('SET_PREBUILT_GRID_EDITOR_FILTER', ['Studio Editor', 'Classic Drag & Drop', 'Custom Code'])
      const order = {
        direction: this.prebuiltItems[0].direction,
        column: this.prebuiltItems[0].name
      }
      this.$store.dispatch('setActiveType', name)
      this.$store.commit('SET_GLOBAL_PAGING', { order, page: 1 })
      Vue.nextTick(() => {
        this.refresh(true)
      })
    },
    checkCompanyGlobalPermissions () {
      const externalId = this.$route.query.externalId
      const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : null
      this.$store.dispatch('setCompanyGlobalPermission', { externalId, userEmail, showLoading: true })
    },
    setUserGlobalPermission () {
      const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : null
      this.$store.dispatch('setUserGlobalPermission', { userEmail, showLoading: true })
    },
    editorFilterRemoved (args) {
      this.$store.commit('SET_PREBUILT_GRID_EDITOR_FILTER', this.$refs.prebuiltMultiSelectFilter.ej2Instances.value)
      this.refresh()
    },
    editorFilterAdded (args) {
      // Only performing multi select commit and grid refresh if the syncfusion element was interacted
      if (args.isInteracted) {
        this.$store.commit('SET_PREBUILT_GRID_EDITOR_FILTER', this.$refs.prebuiltMultiSelectFilter.ej2Instances.value)
        this.refresh()
      }
    }
  }
}
</script>

<style lang="scss" scoped>

  .e-control {
    font-size: 1.6rem;
    font-family: 'Source Sans Pro';
    color: #666;
  }
  .cards {
    flex-flow: row wrap;
    margin-left: -15px;
    margin-right: -15px;
    &::after{
      content: "";
      flex: auto;
    }
    &__container {
      min-height: 349px;
      position: relative;
    }
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1000;
    display: none;
    background-color: #ffffffcf;
    &.active {
      display: block;
    }
  }

  .e-pager{
    border: 0;
    padding: 0;
  }

  .e-icons {
    color: #00ffff;
    font-size: 26px;
  }

  .e-search:before {
    content: '\e993';
  }

  .e-input-group {
    flex-basis: 25%;
    height: 30.5px;
    overflow: hidden;
    &-icon {
      #btnSearch {
        display: block;
        width: 100%;
        height: 100%;
        border-radius: 0;
        border: 0;
        background-color: transparent;
        color: $black;
      }
    }
  }

  #inputSearch {
    height: 100%;
  }

  .e-input-group-icon {
    width: 33px;
    &:hover {
      border-color: $gray-400 !important;
    }
  }

  .dropdown-border-color {
    border-color: #ced4da;
  }

  .sort-by {
    vertical-align: middle;
    line-height: 30px;
    margin-bottom: .25rem;
  }

  .dropdown-sort-by {
    height: calc(1.5em + .75rem + 2px);
    margin-left: 5px;
    font-size: 1.4rem;
    padding: .25rem .5rem;
    border: 1px solid #ced4da;
    border-radius: .25rem;
    min-width: 0px;
    padding-left: 10px;
  }

  .dropdown-sort-by > .e-icon-right {
      margin-left: 10px;
  }

  .dropdown-sort-by > :first-child {
    display: none;
  }

  .dropdown-sort-by > :last-child {
    margin-left: 10px;
  }

  .multiselect-filter-select{
    max-width: 25rem;
    flex-basis: 30%;
    height: 30.5px;
    overflow: hidden;
    height: 3rem !important;
    width: 47rem !important;
  }

  .multi-select-parent{
    // Overriding css height and width, otherwise syncfusion multiselect breaks
    height: 31.5px !important;
    width: 52rem !important;
  }
  .editor-filter {
    vertical-align: middle;
    line-height: 30px;
    margin-bottom: .25rem;
    padding-right: 1rem;
  }
  .flex-parent{
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .switch-parent{
    flex: 0 1 auto;
  }
  .dropdown-filter-parent{
    flex: 0 1 auto;
  }
  .search-parent{
    flex: 2 0 auto;
  }
  .filter-parent{
    flex: 0 1 auto;
  }
</style>
